<template>
  <v-dialog v-model="modalData.dialog" max-width="75%" persistent scrollable>
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        v-if="permissionCan('create')"
        color="primary"
        dark
        class="mb-2"
        v-bind="attrs"
        v-on="on"
        @click="$emit('new')"
      >
        New asset
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <v-row>
          <v-col cols="12">
            <span class="headline">{{ formTitle }}</span>
            <span v-if="formModel.name" class="ml-2 font-weight-medium">
              {{ formModel.name }} - {{ formModel.license_number }}
            </span>
            <v-btn
              icon
              color="#26223c"
              style="float: right; cursor: pointer"
              @click="handleCloseModalForm"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <v-card-text>
        <v-form ref="form" v-model="formValid" enctype="multipart/form-data">
          <v-container>
            <v-row>
              <v-spacer />
              <v-col cols="12" sm="3" md="3">
                <v-select
                  v-if="showStatusSelect"
                  v-model="formModel.status"
                  :items="statusItems"
                  label="Status"
                  item-text="value"
                  item-value="key"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="formModel.asset_type"
                  :items="assetTypeOptions"
                  item-text="text"
                  item-value="value"
                  label="Asset type *"
                  :rules="requiredRules"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="formModel.name"
                  label="Name *"
                  :rules="requiredRules"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-text-field
                  v-model="formModel.license_number"
                  :label="`${assetIdentifierLabel} *`"
                  :rules="requiredRules"
                />
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="formModel.site_id"
                  :items="siteCollection"
                  label="Site *"
                  item-text="name"
                  item-value="id"
                  :id="dynamicID"
                  :rules="requiredRules"
                />
              </v-col>
            </v-row>

            <template v-if="usesOdometer">
              <v-divider class="my-4" />
              <h3 class="mb-3">Road vehicle maintenance</h3>

              <v-row>
                <v-col cols="12" md="4">
                  <label for="datepicker-expiration_date">MOT validity date</label>
                  <b-form-datepicker
                    id="datepicker-expiration_date"
                    v-model="formModel.mot_validity_date"
                    :min="minDate"
                    calendar-width="100%"
                    locale="en"
                    start-weekday="1"
                    class="mb-2"
                    @context="messages.mot_validity_date = ''"
                  />
                  <v-text-field
                    v-model="formModel.mot_notify_before_days"
                    label="MOT notify before days"
                    :rules="nonNegativeRules"
                    type="number"
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="formModel.odometer_km"
                    label="Odometer Km"
                    :rules="nonNegativeRules"
                    type="number"
                  />
                  <v-text-field
                    v-model="formModel.service_interval_km"
                    label="General service interval Km"
                    :rules="positiveOptionalRules"
                    type="number"
                  />
                  <v-text-field
                    v-model="formModel.last_service_km"
                    label="Last general service Km"
                    :rules="nonNegativeRules"
                    type="number"
                  />
                  <v-text-field
                    :value="formatDate(formModel.last_service_date)"
                    label="Last general service date"
                    disabled
                  />
                </v-col>

                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="formModel.service_notify_before_km"
                    label="General service notify before Km"
                    :rules="nonNegativeRules"
                    type="number"
                  />
                  <v-text-field
                    :value="formatKm(formModel.next_service_km)"
                    label="Next general service"
                    disabled
                  />
                  <v-text-field
                    :value="formatKm(formModel.km_to_service)"
                    label="Km to general service"
                    disabled
                  />
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="6">
                  <v-card outlined class="pa-4 fill-height">
                    <h4 class="mb-3">Oil change</h4>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="formModel.oil_change_interval_km"
                          label="Oil change interval Km"
                          :rules="positiveOptionalRules"
                          type="number"
                        />
                        <v-text-field
                          v-model="formModel.last_oil_change_km"
                          label="Last oil change Km"
                          :rules="nonNegativeRules"
                          type="number"
                        />
                        <v-text-field
                          :value="formatDate(formModel.last_oil_change_date)"
                          label="Last oil change date"
                          disabled
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="formModel.oil_change_notify_before_km"
                          label="Notify before Km"
                          :rules="nonNegativeRules"
                          type="number"
                        />
                        <v-text-field
                          :value="formatKm(formModel.next_oil_change_km)"
                          label="Next oil change"
                          disabled
                        />
                        <v-text-field
                          :value="formatKm(formModel.km_to_oil_change)"
                          label="Km remaining"
                          disabled
                        />
                      </v-col>
                    </v-row>
                    <small>Record completed oil changes in Vehicle History with type “Oil change”.</small>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card outlined class="pa-4 fill-height">
                    <h4 class="mb-3">Timing belt</h4>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="formModel.timing_belt_interval_km"
                          label="Timing belt interval Km"
                          :rules="positiveOptionalRules"
                          type="number"
                        />
                        <v-text-field
                          v-model="formModel.last_timing_belt_change_km"
                          label="Last timing belt change Km"
                          :rules="nonNegativeRules"
                          type="number"
                        />
                        <v-text-field
                          :value="formatDate(formModel.last_timing_belt_change_date)"
                          label="Last timing belt change date"
                          disabled
                        />
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-text-field
                          v-model="formModel.timing_belt_notify_before_km"
                          label="Notify before Km"
                          :rules="nonNegativeRules"
                          type="number"
                        />
                        <v-text-field
                          :value="formatKm(formModel.next_timing_belt_km)"
                          label="Next timing belt service"
                          disabled
                        />
                        <v-text-field
                          :value="formatKm(formModel.km_to_timing_belt)"
                          label="Km remaining"
                          disabled
                        />
                      </v-col>
                    </v-row>
                    <small>Record completed timing belt work in Vehicle History with type “Timing belt”.</small>
                  </v-card>
                </v-col>
              </v-row>
            </template>

            <v-divider class="my-4" />
            <h3 class="mb-3">Documents</h3>

            <v-row>
              <v-col cols="12">
                <div class="d-flex flex-wrap align-start attachment-upload-row">
                  <v-file-input
                    v-model="upload.files"
                    label="Choose file(s)..."
                    dense
                    outlined
                    show-size
                    multiple
                    :rules="[filesRule]"
                    @change="onFileSelected"
                  >
                    <template v-slot:selection="{ index, text }">
                      <v-chip
                        v-if="index < 2"
                        color="deep-purple accent-4"
                        dark
                        label
                        small
                      >
                        {{ text }}
                      </v-chip>
                      <span
                        v-else-if="index === 2"
                        class="text-overline grey--text text--darken-3 mx-2"
                      >
                        +{{ uploadFilesCount - 2 }} file(s)
                      </span>
                    </template>
                  </v-file-input>

                  <v-select
                    v-model="upload.category"
                    :items="categoryOptions"
                    item-text="text"
                    item-value="value"
                    label="Category"
                    dense
                    outlined
                    clearable
                  />

                  <v-btn
                    color="primary"
                    :loading="uploading"
                    :disabled="!canUpload"
                    @click="uploadAttachment"
                  >
                    Upload
                  </v-btn>
                </div>
                <small v-if="!formModel.id">Save the asset before uploading documents.</small>
              </v-col>

              <v-col v-for="group in attachmentsByCategory" :key="group.key" cols="12" md="6">
                <v-card outlined class="pa-3 fill-height">
                  <div class="text-subtitle-1 font-weight-bold mb-2">
                    {{ group.label }} ({{ group.items.length }})
                  </div>
                  <v-list dense>
                    <v-list-item v-for="attachment in group.items" :key="attachment.id">
                      <v-list-item-content>
                        <v-list-item-title>
                          {{ attachmentDisplayName(attachment) }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          <v-img
                            lazy-src="public/placeholder/placeholder_img.jpg"
                            max-height="120"
                            max-width="220"
                            :src="attachment.path ? getFile(attachment) : ''"
                            contain
                            class="image-hover"
                            @click="openPublicFile(attachment)"
                          />
                          {{ attachment.mime_type }} • {{ attachmentSize(attachment) }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-btn icon @click="handleFileRemove(formModel, attachment)">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>
                      </v-list-item-action>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-col>
            </v-row>
          </v-container>
        </v-form>

        <v-container v-if="formModel.id">
          <v-divider class="my-4" />
          <v-row>
            <v-col cols="12">
              <v-btn tile class="mb-2 button-hover" @click.stop="openHistory">
                Create history <v-icon>mdi-plus</v-icon>
              </v-btn>
              <h3>Histories</h3>

              <v-data-table
                :headers="historyHeaders"
                :items="histories"
                :items-per-page="5"
                class="elevation-1 cursor-pointer"
                dense
                @click:row="updateHistory"
              >
                <template v-slot:item.odometer_km="{ item }">
                  <span>{{ usesOdometer ? formatKm(item.odometer_km) : '-' }}</span>
                </template>
                <template v-slot:item.type="{ item }">
                  <span>{{ historyTypeLabel(item.type) }}</span>
                </template>
                <template v-slot:item.occurred_at="{ item }">
                  <span>{{ formatDate(item.occurred_at) }}</span>
                </template>
                <template v-slot:item.documents="{ item }">
                  <div v-if="historyAttachments(item).length">
                    <v-btn
                      v-for="attachment in historyAttachments(item)"
                      :key="attachment.id"
                      small
                      text
                      color="primary"
                      class="my-1 d-block"
                      @click.stop="openPublicFile(attachment)"
                    >
                      <v-icon small class="mr-1">mdi-paperclip</v-icon>
                      {{ attachmentDisplayName(attachment) }}
                    </v-btn>
                  </div>
                  <span v-else>-</span>
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-icon small class="mr-2 edit-icon" @click.stop="updateHistory(item.id)">
                    mdi-pencil
                  </v-icon>
                  <v-icon small class="remove-icon" @click.stop="deleteHistory(item.id)">
                    mdi-delete
                  </v-icon>
                </template>
              </v-data-table>

              <VehicleHistoryModal
                :hModalData="historyModalData"
                :historyattachments="vehicleHistoryAttachments"
                :permissions="permissions"
                :asset-type="formModel.asset_type"
                :getFile="getFile"
                :openPublicFile="openPublicFile"
                @history-saved="onHistorySaved"
                @history-attachment-deleted="onHistoryAttachmentDeleted"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn color="blue darken-1" text @click="handleCloseModalForm">
          {{ $t("FORMS.cancel") }}
        </v-btn>
        <v-btn
          v-if="(permissionCan('create') && !formModel.id) || permissionCan('update')"
          color="blue darken-1"
          text
          @click="handleSaveModalForm"
        >
          {{ $t("FORMS.save") }}
        </v-btn>
      </v-card-actions>
    </v-card>

    <v-overlay :value="loader">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
  </v-dialog>
</template>

<style>
.remove-icon,
.edit-icon {
  transition: 0.3s all ease;
}
.remove-icon:hover {
  color: red !important;
}
.edit-icon:hover {
  color: rgb(255, 162, 0) !important;
}
.button-hover:hover {
  color: white !important;
}
.image-hover:hover {
  cursor: pointer;
  opacity: 0.8;
}
.attachment-upload-row {
  gap: 12px;
}
.attachment-upload-row .v-input {
  flex: 1 1 280px;
}
</style>

<script>
import { mapActions, mapGetters } from "vuex";
import Swal from "sweetalert2";
import ApiService from "@/core/services/api.service";
import i18nService from "@/core/services/i18n.service.js";
import { formModelMixins } from "@/view/mixins/formModelMixins.js";
import VehicleHistoryModal from "./VehicleHistoryModal";

const TRANSLATED_ATRIBUTES = [];
const VEH_ENDPOINT = "transport/vehicle";
const HISTORY_ENDPOINT = "transport/vehicleHistory";
const ENDPOINT = "transport/vehicle/";
const PERMISSION_TO = "transport.vehicle.";

export const initialFormData = () => ({
  id: null,
  asset_type: "vehicle",
  license_number: "",
  site_id: null,
  name: "",
  mot_validity_date: null,
  status: 1,
  odometer_km: null,
  service_interval_km: null,
  last_service_km: null,
  last_service_date: null,
  service_type: null,
  service_notify_before_km: null,
  mot_notify_before_days: null,
  next_service_km: null,
  km_to_service: null,
  oil_change_interval_km: null,
  last_oil_change_km: null,
  last_oil_change_date: null,
  oil_change_notify_before_km: null,
  next_oil_change_km: null,
  km_to_oil_change: null,
  timing_belt_interval_km: null,
  last_timing_belt_change_km: null,
  last_timing_belt_change_date: null,
  timing_belt_notify_before_km: null,
  next_timing_belt_km: null,
  km_to_timing_belt: null,
  comment: "",
  translations: [],
  files: [],
  attachments: [],
  file_id: null,
  vehicle_category: null,
});

export default {
  name: "VehicleModalForm",
  components: { VehicleHistoryModal },
  mixins: [formModelMixins],
  props: ["modalData", "permissions"],
  data() {
    return {
      loader: false,
      languages: i18nService.languages,
      selectedLocale: i18nService.languages.find((item) => item.lang === i18nService.getActiveLanguage()),
      formModel: initialFormData(),
      historyModalData: { dialog: false, editedId: null, data: null },
      permissionTo: PERMISSION_TO,
      endPoint: ENDPOINT,
      formValid: false,
      histories: [],
      messages: {},
      allowedExt: ["pdf", "png", "jpg", "jpeg", "webp"],
      assetTypeOptions: [
        { text: "Vehicle", value: "vehicle" },
        { text: "Drone", value: "drone" },
      ],
      categoryOptions: [
        { text: "Technical", value: "technical" },
        { text: "Registration", value: "registration" },
        { text: "Insurance", value: "insurance" },
        { text: "Decision / authority document", value: "decision" },
        { text: "Permit", value: "permit" },
        { text: "Operation manual", value: "operation_manual" },
        { text: "Maintenance", value: "maintenance" },
        { text: "Other document", value: "other" },
      ],
      categoryLabels: {
        technical: "Technical",
        registration: "Registration",
        insurance: "Insurance",
        decision: "Decision / authority documents",
        permit: "Permits",
        operation_manual: "Operation manuals",
        maintenance: "Maintenance documents",
        other: "Other documents",
        uncategorized: "Uncategorized",
      },
      categoryOrder: [
        "registration",
        "insurance",
        "decision",
        "permit",
        "technical",
        "operation_manual",
        "maintenance",
        "other",
        "uncategorized",
      ],
      upload: { files: [], category: null },
      uploading: false,
      requiredRules: [(value) => !!value || "This field is required"],
      nonNegativeRules: [
        (value) => value === null
          || value === ""
          || (!isNaN(Number(value)) && Number(value) >= 0)
          || "Value must be a non-negative number",
      ],
      positiveOptionalRules: [
        (value) => value === null
          || value === ""
          || (!isNaN(Number(value)) && Number(value) > 0)
          || "Value must be a positive number",
      ],
      historyHeaders: [
        { text: "Title", value: "title" },
        { text: "Odometer", value: "odometer_km" },
        { text: "Type", value: "type" },
        { text: "Occurred at", value: "occurred_at" },
        { text: "Documents", value: "documents", sortable: false },
        { text: "Actions", value: "actions", sortable: false, align: "end" },
      ],
      historyTypeLabels: {
        activity: "Activity / mileage",
        service: "General service",
        oil_change: "Oil change",
        timing_belt: "Timing belt",
        repair: "Repair",
        tire: "Tire",
        document: "Document",
        other: "Other",
      },
    };
  },
  computed: {
    ...mapGetters(["siteCollection", "statuses"]),
    translatedAttributes() {
      return TRANSLATED_ATRIBUTES;
    },
    initialFormData() {
      return initialFormData;
    },
    dynamicID() {
      return `vehicle-${Math.random().toString(36).slice(2)}`;
    },
    minDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 20);
      return date.toISOString().slice(0, 10);
    },
    isDrone() {
      return this.formModel.asset_type === "drone";
    },
    usesOdometer() {
      return !this.isDrone;
    },
    formTitle() {
      if (this.formModel.id) return this.isDrone ? "Edit drone" : "Edit vehicle";
      return this.isDrone ? "New drone" : "New vehicle";
    },
    assetIdentifierLabel() {
      return this.isDrone ? "Registration / serial number" : "License number";
    },
    canUpload() {
      return !!this.formModel.id
        && Array.isArray(this.upload.files)
        && this.upload.files.length > 0
        && !!this.upload.category;
    },
    uploadFilesCount() {
      return Array.isArray(this.upload.files) ? this.upload.files.length : 0;
    },
    showStatusSelect() {
      return !!(this.statuses && this.statuses.system);
    },
    statusItems() {
      if (!this.statuses || !this.statuses.companySettings) return [];
      return this.statuses.companySettings.department || [];
    },
    attachmentsByCategory() {
      const directAttachments = (this.formModel.attachments || []).filter((attachment) => {
        const historyId = attachment.vhistory_id !== undefined
          ? attachment.vhistory_id
          : attachment.history_id;
        return historyId === null || historyId === undefined;
      });

      const grouped = directAttachments.reduce((result, attachment) => {
        const key = attachment.vehicle_category || "uncategorized";
        if (!result[key]) result[key] = [];
        result[key].push(attachment);
        return result;
      }, {});

      return this.categoryOrder
        .filter((key) => grouped[key] && grouped[key].length)
        .map((key) => ({
          key,
          label: this.categoryLabels[key] || key,
          items: grouped[key],
        }));
    },
    vehicleHistoryAttachments() {
      return (this.formModel.attachments || []).filter((attachment) => {
        const historyId = attachment.vhistory_id !== undefined
          ? attachment.vhistory_id
          : attachment.history_id;
        return historyId !== null && historyId !== undefined;
      });
    },
  },
  watch: {
    modalData: {
      deep: true,
      immediate: true,
      handler(value) {
        if (!value || !value.dialog) return;

        this.selectedLocale = i18nService.languages.find(
          (item) => item.lang === i18nService.getActiveLanguage()
        );

        if (value.editedId) {
          this.loadVehicle(value.editedId);
          this.fetchHistories(value.editedId);
        } else {
          this.formModel = initialFormData();
          this.histories = [];
          this.upload = { files: [], category: null };
          this.setTranslatedAttributes();
          this.resetErrorMessages();
          this.$nextTick(() => {
            if (this.$refs.form) this.$refs.form.resetValidation();
          });
        }
      },
    },
    "formModel.asset_type"(type) {
      if (type === "drone") this.clearVehicleOnlyFields();
    },
  },
  methods: {
    ...mapActions(["fetchSite"]),
    loadVehicle(vehicleId) {
      return ApiService.get(VEH_ENDPOINT, vehicleId)
        .then(({ data }) => {
          this.formModel = {
            ...initialFormData(),
            ...data,
            asset_type: data.asset_type || "vehicle",
            attachments: Array.isArray(data.attachments) ? data.attachments : [],
            files: [],
          };
          this.setTranslatedAttributes();
          return this.formModel;
        })
        .catch((error) => {
          console.error("Vehicle loading failed:", error);
          return false;
        });
    },
    clearVehicleOnlyFields() {
      [
        "mot_validity_date",
        "odometer_km",
        "service_interval_km",
        "last_service_km",
        "last_service_date",
        "service_type",
        "service_notify_before_km",
        "mot_notify_before_days",
        "oil_change_interval_km",
        "last_oil_change_km",
        "last_oil_change_date",
        "oil_change_notify_before_km",
        "timing_belt_interval_km",
        "last_timing_belt_change_km",
        "last_timing_belt_change_date",
        "timing_belt_notify_before_km",
      ].forEach((field) => { this.formModel[field] = null; });
    },
    formatKm(value) {
      if (value === null || value === undefined || value === "") return "-";
      return `${Number(value).toLocaleString("hu-HU")} km`;
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleDateString("hu-HU") : "-";
    },
    historyTypeLabel(type) {
      return this.historyTypeLabels[type] || type || "-";
    },
    attachmentDisplayName(attachment) {
      if (!attachment) return "Document";
      if (attachment.original_name) return attachment.original_name;
      if (attachment.filename) return attachment.filename;
      const path = String(attachment.path || "").split("?")[0];
      return path.split("/").pop() || attachment.mime_type || "Document";
    },
    attachmentSize(attachment) {
      const size = Number(attachment && attachment.size);
      return Number.isFinite(size) ? `${(size / 1024).toFixed(1)} KB` : "Unknown size";
    },
    isAllowedToOpen(file) {
      const extension = String(file.path || "").toLowerCase().split(".").pop();
      const mime = String(file.mime_type || "").toLowerCase();
      return this.allowedExt.includes(extension)
        || ["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(mime);
    },
    getFile(file) {
      const base = process.env.VUE_APP_BACKEND_URL;
      let path = String(file.path || "").replace(/^\/+/, "");
      if (file.mime_type === "application/pdf") path = "public/placeholder/pdf_placeholder.jpg";
      return `${base}/${path}`;
    },
    openPublicFile(file) {
      if (!this.isAllowedToOpen(file)) {
        Swal.fire({
          icon: "warning",
          title: "Not supported",
          text: "Supported types: jpg, jpeg, png, pdf, webp",
          confirmButtonColor: "#e33354",
        });
        return;
      }

      const base = process.env.VUE_APP_BACKEND_URL;
      const path = String(file.path || "").replace(/^\/+/, "");
      window.open(`${base}/${path}`, "_blank");
    },
    filesRule(files) {
      if (!files) return true;
      const selected = Array.isArray(files) ? files : [files];
      const invalid = selected.some((file) => {
        const extension = String(file.name || "").split(".").pop().toLowerCase();
        return !this.allowedExt.includes(extension);
      });
      return invalid ? "You can upload only jpg, jpeg, png, pdf, webp files" : true;
    },
    onFileSelected(files) {
      this.upload.files = Array.isArray(files) ? files : (files ? [files] : []);
    },
    handleFileRemove(model, file) {
      Swal.fire({
        title: "Are you sure you want to delete this file?",
        showCancelButton: true,
        confirmButtonText: "Delete",
        confirmButtonColor: "#00ad00",
        cancelButtonColor: "#e33354",
      }).then((result) => {
        if (!result.isConfirmed) return;

        this.loader = true;
        ApiService.delete(`transport/vehicle/${model.id}/attachments/${file.id}`)
          .then((response) => {
            this.formModel.attachments = response && response.data && response.data.attachments
              ? response.data.attachments
              : (this.formModel.attachments || []).filter((item) => item.id !== file.id);
            Swal.fire("Deleted!", "", "success");
            this.$emit("saveModalForm", { ...this.formModel });
          })
          .catch(() => Swal.fire("Something went wrong!", "", "error"))
          .finally(() => { this.loader = false; });
      });
    },
    handleSaveModalForm() {
      const valid = this.$refs.form && this.$refs.form.validate
        ? this.$refs.form.validate()
        : true;
      if (!valid) return Promise.resolve(false);

      this.resetErrorMessages();
      const model = { ...this.formModel };
      if (model.asset_type === "drone") this.clearVehicleOnlyFields();

      const formData = new FormData();
      Object.keys(this.formModel).forEach((key) => {
        if (["files", "attachments", "translations"].includes(key)) return;
        const value = this.formModel[key];
        if (value === null || value === undefined) return;
        if (typeof value === "object") formData.append(key, JSON.stringify(value));
        else formData.append(key, value);
      });

      const isUpdate = !!this.formModel.id;
      if (isUpdate) formData.append("_method", "PUT");

      this.loader = true;
      const request = isUpdate
        ? ApiService.post(`${VEH_ENDPOINT}/${this.formModel.id}`, formData)
        : ApiService.post(VEH_ENDPOINT, formData);

      return request
        .then(({ data }) => {
          const saved = data && data.data ? data.data : data;
          this.formModel = {
            ...initialFormData(),
            ...this.formModel,
            ...saved,
            attachments: saved && Array.isArray(saved.attachments)
              ? saved.attachments
              : this.formModel.attachments,
            files: [],
          };

          if (!isUpdate) this.histories = [];
          this.$emit("saveModalForm", { ...this.formModel });
          return data;
        })
        .catch((error) => {
          const errors = error && error.response ? error.response.data : null;
          if (errors) {
            Object.keys(errors).forEach((field) => {
              const message = Array.isArray(errors[field]) ? errors[field][0] : errors[field];
              this.setError(field, message);
            });
          }
          return false;
        })
        .finally(() => { this.loader = false; });
    },
    uploadAttachment() {
      if (!this.canUpload) return Promise.resolve(false);

      this.uploading = true;
      const formData = new FormData();
      this.upload.files.forEach((file) => formData.append("files[]", file));
      formData.append("vehicle_category", this.upload.category);

      return ApiService.post(`${VEH_ENDPOINT}/${this.formModel.id}/attachments`, formData)
        .then(({ data }) => {
          if (data && Array.isArray(data.attachments)) {
            this.formModel.attachments = data.attachments;
          } else {
            const uploaded = data && data.data ? data.data : [];
            this.formModel.attachments = [...(this.formModel.attachments || []), ...uploaded];
          }
          this.upload = { files: [], category: null };
          return data;
        })
        .catch((error) => {
          console.error("Upload failed:", error);
          return false;
        })
        .finally(() => { this.uploading = false; });
    },
    openHistory() {
      this.historyModalData = {
        dialog: true,
        editedId: this.formModel.id,
        data: null,
      };
    },
    fetchHistories(vehicleId) {
      if (!vehicleId) return Promise.resolve([]);

      return ApiService.get(VEH_ENDPOINT, `${vehicleId}/history`)
        .then(({ data }) => {
          this.histories = data && data.data ? data.data : data;
          this.histories = Array.isArray(this.histories) ? this.histories : [];

          const directAttachments = (this.formModel.attachments || []).filter((attachment) => {
            const historyId = attachment.vhistory_id !== undefined
              ? attachment.vhistory_id
              : attachment.history_id;
            return historyId === null || historyId === undefined;
          });
          const historyAttachments = this.histories.reduce((all, history) => {
            return all.concat(Array.isArray(history.attachments) ? history.attachments : []);
          }, []);
          this.formModel.attachments = [...directAttachments, ...historyAttachments];
          return this.histories;
        })
        .catch((error) => {
          console.error("History loading failed:", error);
          this.histories = [];
          return [];
        });
    },
    updateHistory(historyOrId) {
      const historyId = typeof historyOrId === "object" ? historyOrId.id : historyOrId;
      const history = this.histories.find((item) => Number(item.id) === Number(historyId));
      if (!history) return;

      this.historyModalData = {
        dialog: true,
        editedId: history.vehicle_id,
        data: history,
      };
    },
    deleteHistory(historyId) {
      Swal.fire({
        title: "Are you sure you want to delete this history?",
        showCancelButton: true,
        confirmButtonText: "Yes",
        confirmButtonColor: "#00ad00",
        cancelButtonColor: "#e33354",
      }).then((result) => {
        if (!result.isConfirmed) return;

        this.loader = true;
        ApiService.delete(`${HISTORY_ENDPOINT}/${historyId}`)
          .then(() => this.fetchHistories(this.formModel.id))
          .then(() => Swal.fire("Deleted!", "", "success"))
          .catch(() => Swal.fire("Something went wrong!", "", "error"))
          .finally(() => { this.loader = false; });
      });
    },
    historyAttachments(item) {
      return item && Array.isArray(item.attachments) ? item.attachments : [];
    },
    async onHistorySaved() {
      await this.loadVehicle(this.formModel.id);
      await this.fetchHistories(this.formModel.id);
      this.$emit("saveModalForm", { ...this.formModel });
    },
    onHistoryAttachmentDeleted(payload) {
      if (!payload || !payload.attachmentId) return;

      this.formModel.attachments = (this.formModel.attachments || []).filter(
        (attachment) => Number(attachment.id) !== Number(payload.attachmentId)
      );

      this.histories = this.histories.map((history) => {
        if (Number(history.id) !== Number(payload.historyId)) return history;
        return { ...history, attachments: payload.attachments || [] };
      });
    },
  },
  mounted() {
    this.setTranslatedAttributes();
    this.fetchSite();
  },
};
</script>
